/**
 * VIP权限系统使用示例页面
 * 展示如何使用各种VIP组件和Hook
 */

import { useVIPContext } from '@/providers/VIPProvider';
import { PermissionGuard, LimitGuard, VIPUpgradeBanner, VIPBadge, VIPCard } from '@/components/vip';

export function VIPExamplePage() {
  const { permissions, getVIPInfo, hasPermission } = useVIPContext();

  // 示例1: 使用权限守卫组件
  const KnowledgeBaseFeature = () => (
    <PermissionGuard feature="knowledge_base">
      <div className="rounded-lg bg-white p-6 shadow">
        <h3 className="text-xl font-bold">知识库功能</h3>
        <p className="mt-2 text-gray-600">这是VIP专属功能...</p>
      </div>
    </PermissionGuard>
  );

  // 示例2: 使用限制守卫组件
  const RoleCreationFeature = () => (
    <LimitGuard feature="role_slots">
      <div className="rounded-lg bg-white p-6 shadow">
        <h3 className="text-xl font-bold">创建角色</h3>
        <p className="mt-2 text-gray-600">创建新的AI角色...</p>
      </div>
    </LimitGuard>
  );

  // 示例3: 使用Hook检查权限
  const VirtualGatewayFeature = () => {
    const canUse = hasPermission('virtual_gateway');

    return (
      <div className="rounded-lg bg-white p-6 shadow">
        <h3 className="text-xl font-bold">虚拟网关</h3>
        {canUse ? (
          <p className="mt-2 text-green-600">✓ 您可以使用此功能</p>
        ) : (
          <p className="mt-2 text-red-600">✗ 需要VIP会员权限</p>
        )}
      </div>
    );
  };

  // 示例4: 使用升级Banner
  const FeatureWithBanner = () => (
    <div>
      <VIPUpgradeBanner feature="knowledge_base" />
      <div className="rounded-lg bg-white p-6 shadow">
        <h3 className="text-xl font-bold">知识库功能</h3>
        <p className="mt-2 text-gray-600">功能内容...</p>
      </div>
    </div>
  );

  if (!permissions) {
    return <div className="p-8 text-center">加载中...</div>;
  }

  const vipInfo = getVIPInfo();

  return (
    <div className="min-h-screen bg-gray-50 p-8">
      <div className="mx-auto max-w-6xl">
        <h1 className="mb-8 text-3xl font-bold">VIP权限系统示例</h1>

        {/* VIP信息卡片 */}
        <div className="mb-8">
          <VIPCard
            level={vipInfo.level}
            name={vipInfo.name}
            expireAt={vipInfo.expireAt}
            daysRemaining={vipInfo.daysRemaining}
          />
        </div>

        {/* 权限统计 */}
        <div className="mb-8 grid gap-4 md:grid-cols-3">
          <div className="rounded-lg bg-white p-6 shadow">
            <h3 className="font-semibold text-gray-900">角色卡槽</h3>
            <p className="mt-2 text-3xl font-bold text-blue-600">
              {permissions.usage.role_count} / {permissions.limits.role_slots === -1 ? '无限制' : permissions.limits.role_slots}
            </p>
          </div>
          <div className="rounded-lg bg-white p-6 shadow">
            <h3 className="font-semibold text-gray-900">长期记忆</h3>
            <p className="mt-2 text-3xl font-bold text-green-600">
              {permissions.usage.memory_count} / {permissions.limits.memory_count === -1 ? '无限制' : permissions.limits.memory_count}
            </p>
          </div>
          <div className="rounded-lg bg-white p-6 shadow">
            <h3 className="font-semibold text-gray-900">知识库文件</h3>
            <p className="mt-2 text-3xl font-bold text-purple-600">
              {permissions.usage.kb_file_count} / {permissions.limits.kb_file_count === -1 ? '无限制' : permissions.limits.kb_file_count}
            </p>
          </div>
        </div>

        {/* 功能权限列表 */}
        <div className="mb-8">
          <h2 className="mb-4 text-2xl font-bold">功能权限</h2>
          <div className="grid gap-3 md:grid-cols-2">
            <FeatureItem name="文字聊天" allowed={permissions.permissions.text_chat} />
            <FeatureItem name="语音聊天" allowed={permissions.permissions.voice_chat} />
            <FeatureItem name="语音通话" allowed={permissions.permissions.phone_call} />
            <FeatureItem name="知识库" allowed={permissions.permissions.knowledge_base} vipRequired />
            <FeatureItem name="长期记忆" allowed={permissions.permissions.long_memory} />
            <FeatureItem name="虚拟戒指" allowed={permissions.permissions.virtual_ring} />
            <FeatureItem name="虚拟网关" allowed={permissions.permissions.virtual_gateway} vipRequired />
            <FeatureItem name="MCP工具" allowed={permissions.permissions.mcp_tools !== 'basic'} />
          </div>
        </div>

        {/* 示例组件 */}
        <div className="space-y-8">
          <h2 className="text-2xl font-bold">使用示例</h2>

          <section>
            <h3 className="mb-4 text-xl font-semibold">示例1: 权限守卫组件</h3>
            <KnowledgeBaseFeature />
          </section>

          <section>
            <h3 className="mb-4 text-xl font-semibold">示例2: 限制守卫组件</h3>
            <RoleCreationFeature />
          </section>

          <section>
            <h3 className="mb-4 text-xl font-semibold">示例3: 使用Hook检查权限</h3>
            <VirtualGatewayFeature />
          </section>

          <section>
            <h3 className="mb-4 text-xl font-semibold">示例4: 升级Banner</h3>
            <FeatureWithBanner />
          </section>
        </div>
      </div>
    </div>
  );
}

/**
 * 功能权限展示项
 */
interface FeatureItemProps {
  name: string;
  allowed: boolean;
  vipRequired?: boolean;
}

function FeatureItem({ name, allowed, vipRequired }: FeatureItemProps) {
  return (
    <div className={`flex items-center justify-between rounded-lg border p-4 ${allowed ? 'bg-white' : 'bg-gray-100'}`}>
      <span className={allowed ? 'text-gray-900' : 'text-gray-500'}>{name}</span>
      <div className="flex items-center gap-2">
        {vipRequired && <VIPBadge level={1} size="small" />}
        {allowed ? (
          <span className="text-green-600">✓ 可用</span>
        ) : (
          <span className="text-red-600">✗ 不可用</span>
        )}
      </div>
    </div>
  );
}
